/**
 * Page "Mon abonnement" — Admin Agence (lecture seule en Phase 1)
 *
 * Affiche :
 *  - Plan + modules actifs (badges)
 *  - Quotas (users / biens / contacts / IA / stockage) avec barres de progression
 *  - Usage IA du mois en cours (consommé / quota)
 *  - Diffusion portails activés
 *  - CTA "Demander une évolution" → mailto:
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useSubscription } from '../lib/featureFlags';
import { formatNumberFR } from '../utils/formatFR';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { authHeaderOrEmpty } from '../utils/authHeaders';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPct = (used, max) => {
  if (!max || max <= 0) return 0;
  return Math.min(100, Math.round((used / max) * 100));
};

const QuotaBar = ({ label, used, max, unit = '', tone = 'blue' }) => {
  const pct = formatPct(used, max);
  const toneClass = {
    blue: 'bg-blue-500',
    amber: 'bg-amber-500',
    rose: 'bg-rose-500',
    emerald: 'bg-emerald-500',
  }[tone] || 'bg-blue-500';
  const isHigh = pct >= 80;
  const isFull = pct >= 100;
  return (
    <div className="mb-4" data-testid={`quota-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-semibold text-slate-700">{label}</span>
        <span className={`text-xs font-mono num ${isFull ? 'text-rose-600' : isHigh ? 'text-amber-600' : 'text-slate-500'}`}>
          {formatNumberFR(used)}{unit} / {formatNumberFR(max)}{unit}
        </span>
      </div>
      <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${isFull ? 'bg-rose-500' : isHigh ? 'bg-amber-500' : toneClass}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const MODULE_LABELS = {
  'auth':                 { label: 'Authentification',           icon: 'bi-shield-lock' },
  'multi-tenant':         { label: 'Multi-tenant',               icon: 'bi-building' },
  'dashboard':            { label: 'Tableau de bord',            icon: 'bi-bar-chart' },
  'branding':             { label: 'Branding agence',            icon: 'bi-palette' },
  'properties':           { label: 'Biens / Portefeuille',       icon: 'bi-house' },
  'contacts':             { label: 'Contacts & CRM',             icon: 'bi-people' },
  'tasks':                { label: 'Tâches & rappels',           icon: 'bi-check2-square' },
  'ai-core':              { label: 'IA de base',                 icon: 'bi-stars' },
  'avis-de-valeur':       { label: 'Avis de Valeur IA',          icon: 'bi-graph-up' },
  'documents-ia':         { label: 'Générateurs IA',             icon: 'bi-file-earmark-text' },
  'productivite-externe': { label: 'Workspace / Outlook',        icon: 'bi-google' },
  'marketing':            { label: 'Marketing',                  icon: 'bi-megaphone' },
  'diffusion-portails':   { label: 'Diffusion portails',         icon: 'bi-broadcast' },
  'stats-simulateurs':    { label: 'Statistiques & Simulateurs', icon: 'bi-calculator' },
  'import-export':        { label: 'Import / Export',            icon: 'bi-arrow-left-right' },
  'assistant-vocal':      { label: 'Assistant Vocal',            icon: 'bi-mic' },
  'site-web-ia':          { label: 'Site web vitrine IA',        icon: 'bi-globe' },
};

function WhiteLabelUpsellBanner({ level, agenceId }) {
  const authHeader = React.useCallback(
    () => authHeaderOrEmpty(),
    []
  );
  const [modalOpen, setModalOpen] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [pending, setPending] = React.useState(null); // { target_level, created_at } si demande en cours
  const [statusLoaded, setStatusLoaded] = React.useState(false);
  // Niveau souhaité par défaut : si déjà en niveau 2 → seulement Stricte (3)
  const [wantedLevel, setWantedLevel] = React.useState(level >= 2 ? '3' : '2');
  const [message, setMessage] = React.useState('');

  React.useEffect(() => {
    let active = true;
    axios.get(`${API}/branding/upsell-status`, { headers: authHeader() })
      .then(({ data }) => { if (active) { setPending(data?.has_request ? data : null); setStatusLoaded(true); } })
      .catch(() => { if (active) setStatusLoaded(true); });
    return () => { active = false; };
  }, [authHeader]);

  if (level >= 3) return null;

  const handlePreview = () => {
    axios.post(`${API}/branding/upsell-track`, { action: 'preview', target_level: 3 }, { headers: authHeader() }).catch(() => {});
    window.open(`${window.location.origin}/login?preview=${agenceId}&preview_level=3`, '_blank', 'noopener');
  };

  const handleSubmit = async () => {
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/branding/upsell-interest`,
        { target_level: parseInt(wantedLevel, 10), message: message.trim() || null },
        { headers: authHeader() }
      );
      toast.success("Votre demande a été transmise. L'équipe Komerces vous recontactera sous 48 h ouvrées.");
      setPending({ has_request: true, target_level: parseInt(wantedLevel, 10), created_at: new Date().toISOString() });
      setModalOpen(false);
      void res;
    } catch {
      toast.error("Échec de l'envoi. Réessayez ou contactez support@komerces.com.");
    } finally {
      setSending(false);
    }
  };

  const hasPending = !!pending;

  return (
    <>
      <div
        className="rounded-2xl border border-slate-200 bg-gradient-to-r from-slate-900 to-slate-800 text-white px-6 py-5 flex flex-col lg:flex-row lg:items-center gap-4"
        data-testid="white-label-upsell-banner"
      >
        <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
          <i className="bi bi-palette2 text-xl text-blue-300"></i>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-bold" data-testid="upsell-headline">Passez en marque blanche</h3>
          <p className="text-sm text-slate-300 mt-0.5 max-w-2xl">
            Vos documents PDF et emails sont déjà à vos couleurs et logo. La marque blanche personnalise en plus
            l&apos;<strong className="text-white font-semibold">interface de l&apos;application</strong> : votre logo sur la page de connexion de vos
            collaborateurs, vos couleurs dans toute l&apos;app, votre favicon, et zéro mention Komerces (niveau 3).
          </p>
          {hasPending && (
            <p className="text-sm text-amber-300 mt-2 flex items-center gap-1.5" data-testid="upsell-pending-msg">
              <i className="bi bi-hourglass-split"></i>Demande envoyée — en cours de traitement par l&apos;équipe Komerces.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handlePreview}
            className="px-3.5 py-2 text-sm rounded-lg border border-white/20 text-white hover:bg-white/10 transition flex items-center gap-1.5"
            data-testid="upsell-preview-btn"
          >
            <i className="bi bi-eye"></i>Voir un aperçu
          </button>
          {!hasPending && statusLoaded && (
            <button
              onClick={() => setModalOpen(true)}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition flex items-center gap-1.5"
              data-testid="upsell-request-btn"
            >
              <i className="bi bi-envelope-paper"></i>Demander l&apos;activation
            </button>
          )}
        </div>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="upsell-modal">
          <DialogHeader>
            <DialogTitle>Demander l&apos;activation de la marque blanche</DialogTitle>
            <DialogDescription>
              Vous allez transmettre une demande d&apos;activation du niveau marque blanche à l&apos;équipe Komerces.
              Nous reviendrons vers vous sous 48 h ouvrées pour vous présenter l&apos;offre et activer votre personnalisation.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-semibold text-slate-700">Niveau souhaité</Label>
              <RadioGroup value={wantedLevel} onValueChange={setWantedLevel} className="mt-2 space-y-2">
                {level < 2 && (
                  <div className="flex items-start gap-2.5 rounded-lg border border-slate-200 p-3">
                    <RadioGroupItem value="2" id="lvl-light" className="mt-0.5" data-testid="upsell-level-light" />
                    <Label htmlFor="lvl-light" className="cursor-pointer font-normal">
                      <span className="font-semibold text-slate-800">Light</span>
                      <span className="block text-xs text-slate-500 mt-0.5">Votre logo sur la page de connexion + vos couleurs dans l&apos;app. Footer « Propulsé par Komerces » conservé.</span>
                    </Label>
                  </div>
                )}
                <div className="flex items-start gap-2.5 rounded-lg border border-slate-200 p-3">
                  <RadioGroupItem value="3" id="lvl-strict" className="mt-0.5" data-testid="upsell-level-strict" />
                  <Label htmlFor="lvl-strict" className="cursor-pointer font-normal">
                    <span className="font-semibold text-slate-800">Stricte</span>
                    <span className="block text-xs text-slate-500 mt-0.5">White label total : votre favicon, footer personnalisé, zéro mention Komerces nulle part (app, documents, emails).</span>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div>
              <Label htmlFor="upsell-msg" className="text-sm font-semibold text-slate-700">Message (optionnel)</Label>
              <Textarea
                id="upsell-msg"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Une précision sur votre besoin, un délai souhaité…"
                className="mt-1.5 resize-none"
                rows={3}
                data-testid="upsell-message-input"
              />
            </div>
          </div>

          <DialogFooter>
            <button
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
              data-testid="upsell-cancel-btn"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={sending}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-[#0062CC] hover:bg-[#0056b3] text-white transition flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="upsell-submit-btn"
            >
              {sending ? (<><i className="bi bi-arrow-clockwise animate-spin"></i>Envoi…</>) : (<><i className="bi bi-send"></i>Envoyer ma demande</>)}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function MySubscriptionPage() {
  const { data, loading, error, refresh } = useSubscription();
  const navigate = useNavigate();
  const user = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  }, []);
  const isAdmin = user?.role === 'admin_agence' || user?.role === 'super_admin';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-500">
          <i className="bi bi-arrow-clockwise animate-spin mr-2"></i>Chargement…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-rose-600">Erreur de chargement</div>
      </div>
    );
  }

  const sub = data?.subscription || {};
  const usage = data?.usage || {};
  const counts = data?.counts || {};
  const active = sub.active_modules || [];
  const quotas = sub.quotas || {};
  const diffusion = sub.diffusion_portals || {};
  const brandingLevel = parseInt(sub.branding_level || 1, 10);
  const agenceId = user?.agence_id;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="my-subscription-page">
      {/* Header style Komerces — icône carrée à gauche + titre/sous-titre */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#007BFF] to-[#0056b3] flex items-center justify-center shadow-sm shrink-0">
            <i className="bi bi-credit-card-2-front text-white text-2xl"></i>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Mon abonnement</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {sub.plan_id ? (
                <>Forfait : <span className="font-semibold text-slate-700 capitalize">{sub.plan_id.replace(/-/g, ' ')}</span></>
              ) : sub.legacy ? (
                <span className="text-amber-600">Compte historique — tous modules activés</span>
              ) : 'Pas de forfait défini'}
              {sub.status === 'active' && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  Actif
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={() => navigate('/admin-agence')}
              className="px-4 py-2 text-sm bg-slate-900 hover:bg-slate-800 text-white rounded-lg flex items-center gap-2 shadow-sm transition"
              data-testid="subscription-admin-agence-btn"
              title="Administration de l'agence (utilisateurs, paramètres)"
            >
              <i className="bi bi-gear"></i>Administration agence
            </button>
          )}
          <button
            onClick={refresh}
            className="px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-700 flex items-center gap-2 transition"
            data-testid="subscription-refresh-btn"
          >
            <i className="bi bi-arrow-clockwise"></i>Actualiser
          </button>
        </div>
      </div>

      {/* Bandeau upsell marque blanche (admin agence, niveau < 3) */}
      {isAdmin && agenceId && (
        <WhiteLabelUpsellBanner level={brandingLevel} agenceId={agenceId} />
      )}

      {/* Quotas opérationnels */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-4">Quotas opérationnels</h2>
          <QuotaBar label="Utilisateurs"   used={counts.users || 0}   max={quotas.users_max || 0} />
          <QuotaBar label="Biens actifs"   used={counts.biens || 0}   max={quotas.biens_max || 0} />
          <QuotaBar label="Contacts"       used={counts.contacts || 0} max={quotas.contacts_max || 0} />
          <QuotaBar label="Stockage"       used={0}                    max={quotas.storage_gb_max || 0} unit=" Go" tone="rose" />
          <p className="text-[11px] text-slate-400 mt-3">
            Quotas opérationnels : tolérance +10 % avant blocage.
            Stockage : blocage strict des nouveaux uploads à 100 %.
          </p>
        </div>

        {/* Usage IA */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-4">Crédits IA — {usage.year_month}</h2>
          <QuotaBar
            label="Crédits IA consommés"
            used={usage.ai_credits_used || 0}
            max={usage.ai_credits_quota || 0}
            tone="amber"
          />
          <div className="mt-4 p-3 rounded-lg bg-slate-50 text-xs text-slate-600 leading-relaxed">
            <i className="bi bi-info-circle text-slate-400 mr-1"></i>
            Si vous dépassez le quota, l&apos;IA continue de fonctionner en <span className="font-semibold">mode économique</span> (modèle léger). Pas de coupure.
          </div>
        </div>
      </div>

      {/* Modules actifs */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-4">Modules actifs ({active.length})</h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
          {active.map((slug) => {
            const meta = MODULE_LABELS[slug] || { label: slug, icon: 'bi-puzzle' };
            return (
              <div
                key={slug}
                data-testid={`module-active-${slug}`}
                className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50/50 border border-emerald-100"
              >
                <i className={`${meta.icon} text-emerald-600`}></i>
                <span className="text-sm font-medium text-slate-700">{meta.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Diffusion portails */}
      {active.includes('diffusion-portails') && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-4">Diffusion portails</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {Object.entries(diffusion).map(([key, val]) => (
              <div
                key={key}
                data-testid={`diffusion-portal-${key}`}
                className={`p-4 rounded-lg border ${val?.enabled ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 bg-slate-50'}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-700 capitalize">{key}</span>
                  <span className={`text-xs font-bold px-2 py-1 rounded ${val?.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>
                    {val?.enabled ? 'ACTIF' : 'INACTIF'}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">Plafond : {val?.max_listings || 0} annonces</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA évolution */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-6 text-center">
        <h3 className="text-base font-bold text-slate-800">Besoin d&apos;évoluer ?</h3>
        <p className="text-sm text-slate-600 mt-1 mb-4">
          Vous voulez activer un module ou augmenter un quota ? Contactez-nous.
        </p>
        <a
          href="mailto:support@komerces.com?subject=Demande%20d'évolution%20de%20mon%20abonnement"
          className="inline-flex items-center gap-2 px-5 py-2 bg-[#0062CC] hover:bg-[#0056b3] text-white rounded-lg text-sm font-semibold"
          data-testid="subscription-request-evolution-btn"
        >
          <i className="bi bi-envelope"></i>Demander une évolution
        </a>
      </div>
    </div>
  );
}
